import { parseQuickAdd, type ParsedTask } from "@founderdeck/core";
import { useState } from "react";
import type { Project, TaskInput } from "../lib/api";
import { AiRewrite } from "./AiRewrite";
import { hourLabel, todayKey } from "../lib/date";

function preview(p: ParsedTask, projects: Project[]): string {
  const proj = p.projectId
    ? projects.find((x) => x.id === p.projectId)?.name
    : p.projectHint
      ? `#${p.projectHint}`
      : "no project";
  const bits = [
    proj,
    `P${p.pri ?? 2}`,
    p.doDate ? `do ${p.doDate}` : "backlog",
    p.fromHour != null ? `@ ${hourLabel(p.fromHour)}` : null,
    p.deadline ? `⚑ ${p.deadline}` : null,
    p.estMin ? `~${p.estMin}m` : null,
    p.energy === "high" ? "⚡deep" : null,
    p.mit ? "★ Top-3" : null,
  ].filter(Boolean);
  return `→ ${p.title || "…"} · ${bits.join(" · ")}`;
}

export function QuickAdd({
  projects,
  defaultProjectId,
  onAdd,
}: {
  projects: Project[];
  defaultProjectId?: string | null;
  onAdd: (input: TaskInput & { title: string }) => void;
}) {
  const [value, setValue] = useState("");
  // Rewrite strips the shorthand codes from the visible title, but the user still
  // meant that project/priority/date. We stash the metadata parsed *before* the
  // rewrite and overlay it, so the task keeps it even though the title is clean.
  const [sticky, setSticky] = useState<ParsedTask | null>(null);

  const raw = value.trim() ? parseQuickAdd(value, { today: todayKey(), projects }) : null;
  const parsed = raw ? withSticky(raw, sticky) : null;

  // Called when a rewrite is accepted: remember what the user had typed (its
  // metadata) before the clean text replaces it, then swap in the clean text.
  function acceptRewrite(clean: string) {
    if (raw) setSticky(raw);
    setValue(clean);
  }

  function edit(next: string) {
    setValue(next);
    // Starting over (clearing the field) forgets the stashed metadata.
    if (!next.trim()) setSticky(null);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!parsed || !parsed.title) return;
    onAdd({
      title: parsed.title,
      pri: parsed.pri ?? 2,
      estMin: parsed.estMin ?? 30,
      energy: parsed.energy ?? null,
      doDate: parsed.doDate ?? null,
      fromHour: parsed.fromHour ?? null,
      deadline: parsed.deadline ?? null,
      mit: parsed.mit ?? false,
      projectId: parsed.projectId ?? defaultProjectId ?? null,
    });
    setValue("");
    setSticky(null);
  }

  return (
    <form onSubmit={submit}>
      {/* Anchors the rewrite suggestion panel, which drops below the whole row. */}
      <div className="qadd-wrap">
        <div className="qadd">
          <span className="plus">+</span>
          <input
            value={value}
            onChange={(e) => edit(e.target.value)}
            placeholder="Add a task…  e.g.  #site fix og image p1 ~30m today at 11am deep"
            aria-label="Quick add task"
          />
          {/* "quickadd" mode returns clean text; acceptRewrite keeps the metadata. */}
          <AiRewrite text={value} onAccept={acceptRewrite} defaultMode="quickadd" />
        </div>
        {/* Inside the wrap so the panel drops below it, not on top of it. */}
        <div className="qpreview">
          {parsed ? <b>{preview(parsed, projects)}</b> : ""}
        </div>
      </div>
    </form>
  );
}

/** After a rewrite the visible text carries only the title — all the metadata
 *  comes from what the user originally typed (`s`). We can't merge field-by-field
 *  because the parser fills a *default* priority (P2) for the clean prose, which
 *  would mask the stashed priority; and re-parsing clean prose could also match
 *  stray words as a project. So: title from the fresh parse, everything else from
 *  the stash. */
function withSticky(p: ParsedTask, s: ParsedTask | null): ParsedTask {
  if (!s) return p;
  return { ...s, title: p.title };
}
